import { Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { injectParams } from 'ngxtension/inject-params';
import { catchError, map, of, switchMap } from 'rxjs';

import { tracingLink } from '@app/modules/monitor/models/trace.model';
import { TraceService } from '@app/modules/monitor/services/trace.service';
import { PAGE_ROUTE_MAP, PAGES } from '@app/shared/constants/shared.constant';
import type { JobModel } from '@app/shared/models/job.model';
import { formatCost, simulationSessionId } from '@app/shared/utils/trace-format';

/**
 * What a simulation job spent on model calls, as one line that links to its traces.
 *
 * It sits in the job's header so the cost is there whichever tab the job opens on; the Job Results
 * tab carries the fuller card. It renders nothing for a job with no traces.
 */
@Component({
    selector: 'cor-job-model-usage',
    imports: [RouterLink, TooltipModule],
    template: `
        @if (session(); as session) {
            <a
                class="tw:mr-3 tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:tabular-nums"
                test-id="job-model-usage"
                target="_blank"
                pTooltip="What this job spent on model calls. Opens its traces."
                tooltipPosition="bottom"
                [routerLink]="sessionLink()"
            >
                <span class="tw:text-muted-color">Model cost</span>
                <span class="tw:font-semibold tw:text-color">{{ costs().join(' · ') }}</span>
                <span class="tw:text-muted-color">· {{ session.traceCount }} traces</span>
                <i class="coricon-external-link tw:text-sm tw:text-primary"></i>
            </a>
        }
    `,
})
export class JobModelUsageComponent {
    /** The job; re-read whenever it is refreshed, because a running job is still adding traces. */
    readonly job = input.required<JobModel>();

    private readonly currentProject = injectParams<string>('projectSlug');
    private readonly traceService = inject(TraceService);

    protected readonly session = toSignal(
        toObservable(this.job).pipe(
            switchMap(job =>
                job
                    ? this.traceService.session(simulationSessionId(job.id)).pipe(
                          map(response => response.result.session),
                          // A job that made no traced model call has no session: show nothing
                          catchError(() => of(null)),
                      )
                    : of(null),
            ),
        ),
        { initialValue: null },
    );

    protected readonly costs = computed((): string[] => {
        const totals = this.session()?.costByCurrency ?? [];

        return totals.length > 0
            ? totals.map(total => formatCost(total.costMicros, total.currency))
            : ['—'];
    });

    protected readonly sessionLink = computed((): string[] =>
        tracingLink(
            this.currentProject(),
            PAGE_ROUTE_MAP[PAGES.TRACING],
            'sessions',
            simulationSessionId(this.job().id),
        ),
    );
}
